import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Form, Icon, Segment, Transition } from 'semantic-ui-react';
import './index.scss';
import { useDispatch, useSelector } from 'react-redux';
import { Button, DialerHeader, generateResolver, Notification, OfficeViewDialPad, OfficeViewPhoneMiddle, PhoneButton, STATUS_TYPES, VALIDATORS, yup } from 'dyl-components';
import officeViewActions from 'actions/office_view';
import callActions from 'actions/call';
import pbxConfigActions from 'actions/pbx_config';
import dialerActions from 'actions/dialer';
import tagsCategoryActions from "actions/tag_categories";
import { useController, useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import { MainSidePanelContext } from 'shared/context/MainSidePanelProvider';
import PhoneUtil from 'utils/PhoneUtil';
import NewActivityForm from 'shared/forms/NewActivity';
import contactsActions from 'actions/contacts';
import contactPhoneActions from 'actions/contact_phone';
import contactActions from 'actions/contact';

const getRelationOption = (relation) => {
    const text = relation.related_name || relation.contact_name;
    const [first,] = text.split("-");
    const isLead = first === "Lead ";
    const iconName = {
        customer: 'fas fa-user-crown',
        customer_pipeline: 'fas fa-crown',
        pipeline_stage: isLead ? 'fas fa-address-card' : 'fas fa-filter-circle-dollar'
    }[relation.related_type] || '';
    const related_id = relation.related_id;

    return {
        key: related_id,
        value: related_id,
        text: <>
            <Icon className={iconName} /> {text}
        </>
    };
};

const CampaignDialer = ({ phone }) => {
    const { dialerStatus, contact_lookup, callState, currentCall, currentCallTicker, actionButtons, currentCallUUID, currentCallPhoneNumber, endedCalls, userPhoneCreds, currentCallChannelUUID, currentPBX, callDirection, currentCallType, contactLeg, isReadingTransferDestinations, transferDestinations, isTransferringCall, listOfTagCategories, isGettingListOfTagCategories, isUpdatingCallResults, campaignName, campaign_id, contact_id, ended_uuid, agent_uuid, hasIntegration, isDialerSessionEnding, speaking, user_voicemail_messages, isReadingUserVoicemailMessages } = useSelector(state => {
        let speaking;
        let officeCurrentCall = state.officeView.currentCall;
        const calls = state.officeView.calls;
        const userPhoneCreds = state.officeView.creds.extension;
        if (calls[officeCurrentCall]) {
            const callIDs = PhoneUtil.phoneDeviceCallIDs(calls[officeCurrentCall], phone, userPhoneCreds);
            const session = PhoneUtil.phoneDeviceSession(callIDs.sessionID, phone);
            speaking = session ? !phone?.current?.phone?.isHeld(session) : false;
        }
        const call_uuid = state.officeView.dialer?.call_uuid;
        const ended_uuid = state.officeView.dialer?.ended_uuid;
        const currentCallUUID = call_uuid || ended_uuid;
        const currentCall = calls?.[currentCallUUID];
        const userLeg = currentCall?.legs?.find(l => l.user_id === state.auth.user_id); // need to use user id because users without phone systems can use the dialer
        if (currentCall) {
            const callIDs = PhoneUtil.phoneDeviceCallIDs(currentCall, phone, userPhoneCreds)
            currentCall.session_id = callIDs?.sessionID
            if (currentCall?.session_id) {
                const sess = PhoneUtil.phoneDeviceSession(currentCall.session_id, phone);
                if (! currentCall.actionButtons) {
                    currentCall.actionButtons = {}
                }
                if (sess) {
                    currentCall.actionButtons.hold = phone?.current?.phone?.isHeld(sess);
                    currentCall.actionButtons.mute = phone?.current?.phone?.isMuted(sess);
                } else {
                    delete currentCall.session_id;
                }
            }
        }
        const contact_lookup = state.officeView.dialerContactLookup;
        const dialerStatus = state.officeView.dialer?.status;
        return {
            currentCallUUID,
            callState: (currentCall && call_uuid && currentCall?.status) || dialerStatus,
            dialerStatus,
            contact_lookup,
            currentCall,
            currentCallTicker: state.officeView.currentCallTicker,
            actionButtons: currentCall?.actionButtons,
            currentCallPhoneNumber: contact_lookup?.contact_details?.phone[0]?.phone,
            endedCalls: state.officeView.endedCalls || {},
            userPhoneCreds,
            currentPBX: currentCall?.pbx || state.officeView?.dialer?.pbx || currentCall?.pbx || null,
            currentCallChannelUUID: userLeg?.channel_uuid,
            currentCallType: currentCall?.call_type || null,
            callDirection: currentCall?.direction,
            contactLeg: PhoneUtil.contactLeg(currentCall, userPhoneCreds),
            isReadingTransferDestinations: state.pbx_config.isReadingTransferDestinations,
            transferDestinations: state.pbx_config.transferDestinations,
            isTransferringCall: state.call.isTransferringCall,
            listOfTagCategories: state?.tag_categories?.listOfTagCategories,
            isGettingListOfTagCategories: state?.tag_categories?.isGettingListOfTagCategories,
            isUpdatingCallResults: state.call_history.isUpdatingCallResult || state.officeView.isSavingDialerResults || state.contact.isUpdatingContactPhoneDNC,
            campaignName: state.officeView?.dialer?.campaign_name,
            campaign_id: state.officeView?.dialer?.campaign_id,
            contact_id: currentCall?.legs?.find(l => l.contact_id)?.contact_id || state.officeView?.dialer?.contact_id,
            ended_uuid: state.officeView.dialer?.ended_uuid,
            agent_uuid: state.officeView?.dialer?.agent_uuid,
            hasIntegration: Object.keys(state.oauth_integrations.activeIntegration || {}).length > 0,
            isDialerSessionEnding: state.officeView.isHangingUp,
            speaking,
            isReadingUserVoicemailMessages: state.pbx_config?.isReadingUserVoicemailMessages,
            user_voicemail_messages: state.pbx_config?.user_voicemail_messages?.map(message => ({
                key: message.sound_id,
                value: message.location,
                text: message.label
            }))
        }
    })
    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(tagsCategoryActions.getListOfTagCategories());
        dispatch(pbxConfigActions.getUserVMMessages());
    }, [dispatch])

    const useTicker = useSelector((state) => {
        const activeIdx = Object.values(state?.officeView?.calls)?.findIndex(call => call.connected > 0 || call.company_destination !== '');
        return (typeof activeIdx !== 'undefined') && activeIdx !== -1;
    });
    
    useEffect(() => {
        let tickerId;
        if(!useTicker){
            dispatch(officeViewActions.currentCallTicker('CALL_CURRENT_TICKER', true));
        } else {
            tickerId = setInterval(() => {
                dispatch(officeViewActions.currentCallTicker('CALL_CURRENT_TICKER'));
            }, 1000);
        } 
        return () => clearInterval(tickerId);
    }, [useTicker, dispatch]);

    const { control: transferControl, formState: { isValid: transferValid, isDirty: transferDirty }, getValues: getTransferValues, reset: resetTransfer } = useForm({
        mode: 'onChange',
        defaultValues: {
            phoneNumber: "",
        },
        resolver: generateResolver({
            phoneNumber: VALIDATORS.PHONE_NUMBER()
        })
    });

    const {
        control: resultsControl,
        handleSubmit: resultsHandleSubmit,
        formState: { isValid: resultsIsValid, isDirty: resultsIsDirty },
        reset: resultsReset
    } = useForm({
        mode: "onChange",
        defaultValues: {
            call_result: null,
            call_notes: "",
            call_tag: "",
            related_to: null,
            sub_tag: null,
        },
    });

    useEffect(() => {
        if (currentCall) {
            const { user_result, notes, related_id } = currentCall || {};
            const callTag = currentCall?.tags?.[0] || "";
            const [parent_value] = (callTag?.name || "").split("/");
            const parent_id = listOfTagCategories?.find?.(
                (item) => item?.category === parent_value
            )?.id;
            resultsReset(
                {
                    call_result: user_result || null,
                    call_notes: notes || "",
                    call_tag: parent_id,
                    sub_tag: callTag?.id,
                    related_to: related_id,
                    suspend_call: currentCall?.call_suspended
                },
                { keepDirty: true }
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentCall]);

    const hangup = () => {
        // phone.current.HangUp(); //May add in future, incase BE endpoint fails
        dispatch(officeViewActions.hangup({ pbx_hostname: currentPBX, channel_uuid: contactLeg.channel_uuid || agent_uuid || currentCallChannelUUID })).then(() => {
            resetHoldAndMute();
        });
        dispatch(officeViewActions.actionButtons('CALL_ACTION_BUTTONS', currentCallUUID, { hangup: !actionButtons.hangup }));
    }

    const onVmDrop = (audio) => {
        const contact_id = contactLeg.contact_id;
        dispatch(callActions.vmDrop({
            agent_uuid: currentCallChannelUUID,
            pbx_hostname: currentPBX,
            contact_id: contact_id,
            campaign_id: campaign_id,
            contact_uuid: contactLeg.channel_uuid,
            vm: audio,
            dialer: true
        })).then(() => {
            dispatch(contactActions.readContact(contact_id));
        });
    }

    const transfer = () => {
        dispatch(officeViewActions.actionButtons('CALL_ACTION_BUTTONS', currentCallUUID, { transfer: !actionButtons.transfer }));
        resetTransfer();
    }

    const isDialerCall = currentCallType === "dialer";
    const isDialerCallAnswered = isDialerCall && !currentCall?.ringing_agent;
    const currentCallSessionID = currentCall?.session_id;

    const resetHoldAndMute = () => {
        const session = PhoneUtil.phoneDeviceSession(currentCallSessionID, phone);
        if (session) {
            if (phone.current.phone.isHeld(session)) {
                phone.current.phone.unhold(session);
            }
            if (phone.current.phone.isMuted(session)) {
                phone.current.phone.unmute(session);
            }
        }
    }

    const isCallTransferred = callDirection === "inbound" && currentCall?.call_type === "transfer";
    const isRinging = callState === "ringing" && !isCallTransferred;

    const onHold = () => {
        if (Object.keys(phone?.current?.Sessions || {}).length > 1) {
            phone?.current.holdOtherCalls(currentCallSessionID);
        }
        phone.current.phoneHoldButtonPressed(currentCallSessionID);
        dispatch(officeViewActions.actionButtons('CALL_ACTION_BUTTONS', currentCallUUID, { hold: !actionButtons.hold }));
    }

    const mute = () => {
        phone.current.phoneMuteButtonPressed(currentCallSessionID);
        dispatch(officeViewActions.actionButtons('CALL_ACTION_BUTTONS', currentCallUUID, { mute: !actionButtons.mute }));
    }

    const onClickTransfer = (fs_destination) => {
        dispatch(callActions.transfer({
            caller_id_number: contactLeg.phone,
            pbx_hostname: currentPBX,
            channel_uuid: contactLeg.channel_uuid,
            transfer_to: !!fs_destination ? fs_destination : getTransferValues('phoneNumber'),
            caller_id_name: contact_lookup?.name,
            dialer: true
        }, { transfer_user: contactLeg.user_id })).then(() => {
            resetHoldAndMute();
        });
        dispatch(officeViewActions.actionButtons('CALL_ACTION_BUTTONS', currentCallUUID, { transfer: !actionButtons.transfer }));
    }

    const searchTransfer = async (search) => {
        dispatch(pbxConfigActions.transferDestinations({ search }))
    }

    const answer = () => {
        phone.current.Answer(currentCallChannelUUID);
        dispatch(officeViewActions.actionButtons('CALL_ACTION_BUTTONS', currentCallUUID, { answer: !actionButtons.answer }));
        dispatch(officeViewActions.onClosePhoneNotifications("CLOSE_CALL_NOTIFICATIONS", currentCallChannelUUID));
        if (Object.keys(phone?.current?.Sessions || {}).length > 1) {
            phone?.current.holdOtherCalls(currentCallSessionID);
        }
    }

    const ignore = () => {
        hangup();
        dispatch(officeViewActions.onClosePhoneNotifications("CLOSE_CALL_NOTIFICATIONS", currentCallChannelUUID));
        dispatch(officeViewActions.ignoreDialerCall());
    }

    const getFieldUUID = () => (
        callState === "hangup" ? endedCalls[userPhoneCreds]?.uuid :
            callState === "active" ? currentCall : null
    )

    const onChangeResults = (result) => {
        const uuid = getFieldUUID();
        dispatch(officeViewActions.changeResults('CHANGE_RESULT', uuid, result))
    }

    const onChangeNotes = (notes) => {
        const uuid = getFieldUUID();
        dispatch(officeViewActions.changeNotes('CHANGE_NOTES', uuid, notes))
    }

    const tagsCategoryOptions = useMemo(() => {
        if (!listOfTagCategories?.length) return [];
        return listOfTagCategories?.map?.((category) => {
            return {
                text: category?.category,
                value: category?.id,
                key: category?.id,
                options: category?.tags?.map?.((tag) => ({
                    text: String(tag?.tag),
                    value: tag?.id,
                    key: tag?.id,
                })),
            };
        });
    }, [listOfTagCategories]);

    const getRelatedToOptions = (record) => {
        if (!record) {
            return [];
        }
        const { contact_id, relations } = record;
        if (contact_id) {
            return relations?.map(getRelationOption) || [];
        }
        return [];
    }

    const [isDialPadOpen, setDialPadOpen] = useState(false);
    const { control } = useForm({
        mode: 'onChange',
        defaultValues: {
            dial: "",
        },
        resolver: generateResolver({
            dial: yup.string().minlength(3).required("")
        })
    });
    const { field: dialedNumberField } = useController({
        control,
        name: "dial"
    });

    const getResult = (data) => {;
        return {
            notes: data.call_notes || "",
            result: data.call_result || null,
            tag: data?.sub_tag,
            related_id: data?.related_to || 0,
            call_uuid: ended_uuid,
        }
    };

    const isHungup = callState === "hangup";

    const endDialer = async () => {
        try {
            if (!isHungup) {
                await dispatch(officeViewActions.hangup({
                    pbx_hostname: currentPBX, channel_uuid: agent_uuid || currentCallChannelUUID,
                }));
            }
            await dispatch(officeViewActions.exitDialer());
        } catch (e) {
            console.log(e);
            Notification.alert("Failed to end dialer", STATUS_TYPES.ERROR);
        }
    };

    const clearDialer = () => {
        dispatch(officeViewActions.closeDialer());
        navigate(`/campaigns/${campaign_id}/contacts`);
    }

    const saveResults = async (data) => {
        try {
            await dispatch(dialerActions.end({
                campaign_id,
                contact_id,
                channel_uuid: ended_uuid || currentCallChannelUUID,
                result: getResult(data)
            }));
            if (data.suspend_call) {
                await dispatch(
                    contactPhoneActions.phoneDNC(null, {
                        phone: currentCallPhoneNumber,
                        call_suspended: data?.suspend_call,
                    }),
                );
            }
            navigate(`/campaigns/${campaign_id}/contacts`);
        } catch (e) {
            console.log(e);
            Notification.alert("Failed to save results", STATUS_TYPES.ERROR);
        }
    }

    const navigate = useNavigate();
    const { onCloseSidePanel } = useContext(MainSidePanelContext);

    const next = async (data) => {
        console.log("|+| proceeding to next contact");
        try {
            resetHoldAndMute();
            const { contact_id: nextContact } = await dispatch(dialerActions.next({
                call_uuid: currentCallUUID,
                campaign_id,
                agent_uuid,
                contact_id,
                pbx: currentPBX,
                result: getResult(data)
            }));
            if (data.suspend_call) {
                await dispatch(
                    contactPhoneActions.phoneDNC(null, {
                        phone: currentCallPhoneNumber,
                        call_suspended: data?.suspend_call,
                    }),
                );
            }
            if (nextContact) {
                navigate(`/contact/${nextContact}`);
                dispatch(contactsActions.dialerViewLookup(nextContact));
                onCloseSidePanel();
            }
        } catch (e) {
            console.error(e);
            Notification.alert("Failed to proceed to next contact", STATUS_TYPES.ERROR);
        }
    };
    const answerDisabled = !!!currentCallSessionID || PhoneUtil.isHardPhone(phone);
    const isActive = callState === 'active';

    const [isLogActivityModalOpen, setIsLogActivityModalOpen] = useState(false);

    const activeCallTicker = isActive &&  currentCall?.connected && currentCallTicker > 0 ? currentCallTicker - currentCall?.connected + (currentCall?.drift || 0) : 0;
    const isDialerDone = dialerStatus === "done";
    const isDialerSessionEnded = !agent_uuid;
    const isDialerActive = !isDialerSessionEnded;
    const isLoadingNextContact = dialerStatus === "loading";
    const isCallEnded = callState === "ended" || isCallTransferred;
    const isPress1 = currentCall?.press1;
    const press1 = () => { dispatch(dialerActions.pressed({ hostname: currentPBX, uuid: currentCallUUID })); };
    const isDialerIdle = isCallEnded || callState === "loading";
    const dialerheaderState = isDialerIdle ? "hangup" : callState === 'calling' ? "ringing" : callState;
    const shouldShowDialpadControls = (isDialerActive || isPress1) && !isDialerDone && !isHungup;
    const onToggleDialpad = () => { setDialPadOpen(!isDialPadOpen); };
    return (
        <Segment.Group className='CampaignDialer'>
            <DialerHeader
                {...(shouldShowDialpadControls ? { onToggleDialpad } : {})}
                campaignName={campaignName}
                isDialPadActive={isDialPadOpen && !isDialerIdle && callState !== "hangup"}
                state={dialerheaderState}
                phoneNumber={currentCallPhoneNumber}
            />
            {!isLoadingNextContact ? (
                <Form size='tiny'>
                    <Segment.Group size='tiny'>
                        <Segment>
                        {!isHungup && !isDialerDone && !isCallEnded && (
                            <Segment compact className='Caller' size='tiny'>
                                <Icon className={`fa-solid ${isRinging ? "fa-phone-volume" : "fa-phone-arrow-up-right"} Caller__ringing-icon`} />
                                <div className="Caller__number">{PhoneUtil.asYouTypeFormatter(currentCallPhoneNumber, false)}</div>
                                <div className="Caller__time-ticker">{activeCallTicker ? PhoneUtil.timeTicker(activeCallTicker) : null}</div>
                            </Segment>
                        )}
                        {!isDialerDone && (
                            <>
                                <OfficeViewPhoneMiddle
                                    contact_lookup={contact_lookup}
                                    callState={isCallEnded ? "hangup" : callState}
                                    control={resultsControl}
                                    hangup={hangup}
                                    onVmDrop={onVmDrop}
                                    vmDropDisabled={isRinging || user_voicemail_messages?.length <= 0 || contact_lookup?.voicemail}
                                    transfer={transfer}
                                    transferDisabled={isRinging}
                                    hold={onHold}
                                    holdDisabled={isRinging || !!!currentCallSessionID}
                                    mute={mute}
                                    muteDisabled={isRinging || !!!currentCallSessionID}
                                    onSaveDisabled={isRinging || isActive || !resultsIsDirty || !resultsIsValid}
                                    callResultsDisabled={isRinging || (isActive && callDirection !== "outbound")}
                                    callTagDisabled={isRinging || isGettingListOfTagCategories}
                                    relatedToDisabled={isRinging}
                                    currentCallPhoneNumber={currentCallPhoneNumber}
                                    duration={currentCall?.duration}
                                    callRecording={currentCall?.callRecording}
                                    hasIntegration={hasIntegration}
                                    logActivityForm={(contact, emailOnly, isContact, onClose, hideActivitySelection) => {
                                        return (
                                            <NewActivityForm
                                                contact={contact}
                                                emailOnly={emailOnly}
                                                isContact={isContact}
                                                onClose={onClose}
                                                hideActivitySelection={hideActivitySelection}
                                            />
                                        );
                                    }}
                                    isLogActivityModalOpen={isLogActivityModalOpen}
                                    setIsLogActivityModalOpen={setIsLogActivityModalOpen}
                                    answer={!isPress1 ? answer : press1}
                                    ignore={ignore}
                                    callDirection={callDirection}
                                    userCurrentCallDirection={callDirection}
                                    actionButtons={actionButtons}
                                    currentTime={currentCallTicker}
                                    onChangeResults={onChangeResults}
                                    onChangeNotes={onChangeNotes}
                                    transferControl={transferControl}
                                    onClickTransfer={onClickTransfer}
                                    transferValid={transferValid}
                                    isReadingTransferDestinations={isReadingTransferDestinations}
                                    transferDestinations={transferDestinations}
                                    searchTransfer={searchTransfer}
                                    transferDirty={transferDirty}
                                    isSaveLoading={isUpdatingCallResults}
                                    isTransferringCall={isTransferringCall}
                                    answerDisabled={answerDisabled}
                                    isClickToDial={isDialerCall}
                                    answerClick2Dial={!isPress1 ? answer : press1}
                                    isClickToDialAnswered={isDialerCallAnswered}
                                    isPhoneSystemSupported
                                    currentCall={currentCall}
                                    currentCallUUID={currentCallUUID}
                                    userPhoneCreds={userPhoneCreds}
                                    callTagOptions={tagsCategoryOptions}
                                    relatedToOptions={getRelatedToOptions(contact_lookup)}
                                    isDialer
                                    isPress1={isPress1}
                                    isShowingCallResultsForm={!isDialPadOpen || !shouldShowDialpadControls || isHungup || isCallEnded}
                                    vmDropOptions={user_voicemail_messages}
                                    isReadingVmDropOptions={isReadingUserVoicemailMessages}
                                />
                                {isDialPadOpen && shouldShowDialpadControls && isDialerCallAnswered && (
                                    <div className='Dialer__next-button'>
                                        <Button fluid basic onClick={onToggleDialpad}><Icon className='fas fa-chevron-circle-down' />Call Results</Button>
                                    </div>
                                )}
                                {(isHungup || isCallEnded) && agent_uuid && (
                                    <div className='Dialer__next-button'>
                                        <Button fluid color='primary' onClick={resultsHandleSubmit(next)} disabled={speaking} >Next</Button>
                                    </div>
                                )}
                            </>)}
                        {isDialerDone && (
                            <Segment className='Dialer__complete-message' textAlign='center'>
                                <div>
                                    <strong as='h5'><Icon className='fas fa-party-horn' /> Campaign Completed</strong>
                                </div>
                                <div>No contacts left to call. Keep up the great work!</div>
                            </Segment>
                        )}
                        {(isHungup || (!isDialerSessionEnding && !isPress1 && !(dialerheaderState === 'ringing' && callDirection === 'inbound') && !(callDirection === "outbound" && !isDialerCallAnswered && callState !== 'active'))) && (
                            <div className='Dialer__next-button'>
                                {isDialerActive && !isDialerDone ? (
                                    <PhoneButton small text="End Dialer" iconColor='red' onClick={endDialer} icon='fas fa-arrow-right-from-bracket' />
                                ) : (
                                    <Button 
                                        disabled={isDialerSessionEnding || isUpdatingCallResults} 
                                        fluid 
                                        color='primary'
                                        onClick={isDialerDone ? clearDialer : resultsHandleSubmit(saveResults)}
                                        loading={isUpdatingCallResults}
                                    >
                                        {isDialerSessionEnding ? "Ending..." : `${!isDialerDone ? `Save & ` : ""}Exit Dialer`}
                                    </Button>
                                )}
                            </div>
                        )}
                    </Segment>
                    {shouldShowDialpadControls && !isHungup && !isCallEnded && (
                        <>
                            <Segment>
                                <Icon className='fas fa-dialpad' /> <Icon onClick={onToggleDialpad} link color='primary' className={`fas fa-chevron-circle-${isDialPadOpen ? 'up' : 'down'}`} style={{ float: "right" }} />
                            </Segment>
                            <Transition visible={isDialPadOpen} animation='slide down'>
                                <Segment className={`CampaignDialer__dialpad`}>
                                    <div {...(isActive ? { style: { marginBottom: 96 } } : {})}>
                                        <OfficeViewDialPad
                                            control={control}
                                            dialedNumber={dialedNumberField.value}
                                            onKeyPress={(number) => {
                                                phone.current.sipSendDTMF(number);
                                                if (number === "1" && isPress1) {
                                                    press1();
                                                } 
                                            }}
                                            isDialer
                                        />
                                    </div>
                                </Segment>
                            </Transition>
                        </>
                    )}
                    </Segment.Group>
                </Form>
            ) : (
                <Segment basic textAlign='center'>
                    Moving to next contact... <Icon className='fas fa-circle-notch' loading />
                </Segment>
            )}
        </Segment.Group>
    )
}

const DialerContainer = ({ phone }) => {
    const { dialerContactID, isDialerRunning, isGettingActiveCalls } = useSelector(state => ({
        isDialerRunning: PhoneUtil.isDialerRunning(state.officeView?.dialer),
        dialerContactID: state.officeView?.dialer?.contact_id,
        isGettingActiveCalls: state.officeView?.isGettingActiveCalls
    }))
    const location = useLocation();
    const isInContactProfileBeingCalled = location.pathname?.split('/')[1] === 'contact' && Number(location.pathname?.split('/')[2]) === dialerContactID;
    const shouldShowDialer = isDialerRunning && isInContactProfileBeingCalled;
    
    if (!isDialerRunning || !shouldShowDialer) {
        return null;
    }

    return (
        <Segment basic className="Main__campaign-dialer" loading={isGettingActiveCalls}>
            {!isGettingActiveCalls && <CampaignDialer phone={phone} />}
        </Segment>
    )
}

export default DialerContainer;
